import { useCallback, useState } from 'react';
import {
  approveCarrierRequest,
  listCarrierRequests,
  mergeCarrierRequest,
  rejectCarrierRequest,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { type Notify, Panel, btn, btnGhost, input } from './ui.js';

const PRODUCT_LINES = ['major_medical', 'medicare', 'ancillary'] as const;

/**
 * Operator approval queue for customer "add my carrier" requests (statement-first
 * setup). Approve creates the registry carrier + AI-builds its mapping from the
 * stored PII-safe sample; merge attaches the request to an existing carrier;
 * either decision emails the requesting account. The product-line choice matters:
 * it routes the owed engine (Medicare/ancillary MUST be set correctly).
 */
export function CarrierRequestsPanel({
  carriers,
  notify,
  onDecided,
}: {
  carriers: { id: string; name: string }[];
  notify: Notify;
  onDecided: () => void;
}) {
  const { data, error, reload } = useAsyncData(useCallback(() => listCarrierRequests(), []));
  const requests = data ?? [];
  const pending = requests.filter((r) => r.status === 'pending');
  const decided = requests.filter((r) => r.status !== 'pending').slice(0, 10);
  const [showDecided, setShowDecided] = useState(false);

  // Per-request edit state (name / product line / merge target), keyed by id.
  const [names, setNames] = useState<Record<string, string>>({});
  const [lines, setLines] = useState<Record<string, string>>({});
  const [mergeTargets, setMergeTargets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  // `fn` returns the success toast — so a decision whose mapping build was
  // skipped says so instead of reading like a clean success.
  const decide = async (id: string, fn: () => Promise<string>) => {
    setBusy(id);
    try {
      notify(await fn(), 'success');
      await reload();
      onDecided();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Decision failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (error) return null; // the Carriers page still works without the queue

  return (
    <Panel title={`Carrier requests${pending.length > 0 ? ` (${pending.length} pending)` : ''}`}>
      <p className="mb-3 text-xs text-neutral-500">
        Customer-requested carriers from statement-first setup (the app's "Set up a carrier" page).
        Approve creates the carrier and learns the format from the stored sample shape; merge points
        the request at an existing carrier. The customer is emailed either way. Product line routes
        the owed engine — set it right before approving.
      </p>
      {requests.length === 0 && (
        <p className="text-sm text-neutral-400">
          No carrier requests yet. When a customer uploads a statement from a carrier we don't
          support and requests it, it lands here (and you're emailed).
        </p>
      )}
      {requests.length > 0 && pending.length === 0 && (
        <p className="text-sm text-neutral-400">Nothing pending — all caught up.</p>
      )}
      <div className="space-y-4">
        {pending.map((r) => {
          const name = names[r.id] ?? r.requestedName;
          const line = lines[r.id] ?? (r.inferredProductLine || 'major_medical');
          const mergeTarget = mergeTargets[r.id] ?? '';
          return (
            <div key={r.id} className="rounded-lg border border-neutral-200/70 bg-white p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="text-sm font-semibold">{r.requestedName}</span>
                  <span className="ml-2 text-xs text-neutral-400">
                    from {r.accountName} · {r.fileType.toUpperCase()}
                    {r.fileName ? ` · ${r.fileName}` : ''}
                  </span>
                </div>
                <span className="text-xs text-neutral-400">
                  looks like <span className="font-medium">{r.inferredProductLine ?? '?'}</span>
                </span>
              </div>
              {r.note && <p className="mt-1 text-xs text-neutral-500">“{r.note}”</p>}
              <p className="mt-1 truncate text-xs text-neutral-400" title={r.headers.join(' · ')}>
                Columns: {r.headers.join(' · ')}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  className={`${input} w-56`}
                  value={name}
                  onChange={(e) => setNames((s) => ({ ...s, [r.id]: e.target.value }))}
                  aria-label="Carrier name"
                />
                <select
                  className={`${input} w-40`}
                  value={line}
                  onChange={(e) => setLines((s) => ({ ...s, [r.id]: e.target.value }))}
                  aria-label="Product line"
                >
                  {PRODUCT_LINES.map((pl) => (
                    <option key={pl} value={pl}>
                      {pl}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={btn}
                  disabled={busy === r.id || name.trim().length < 2}
                  onClick={() =>
                    decide(r.id, async () => {
                      const res = await approveCarrierRequest(r.id, {
                        name: name.trim(),
                        productLine: line,
                      });
                      return res.configBuilt
                        ? `Approved — ${name.trim()} created and mapping learned`
                        : `Approved — ${name.trim()} created; mapping build skipped (${res.buildReason ?? 'no AI model'}) — first upload auto-onboards`;
                    })
                  }
                >
                  Approve
                </button>
                <span className="text-xs text-neutral-400">or</span>
                <select
                  className={`${input} w-52`}
                  value={mergeTarget}
                  onChange={(e) => setMergeTargets((s) => ({ ...s, [r.id]: e.target.value }))}
                  aria-label="Merge into existing carrier"
                >
                  <option value="">Merge into existing…</option>
                  {carriers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={btnGhost}
                  disabled={busy === r.id || !mergeTarget}
                  onClick={() =>
                    decide(r.id, async () => {
                      const res = await mergeCarrierRequest(r.id, mergeTarget);
                      return res.configBuilt
                        ? 'Merged — statement shape learned for the existing carrier'
                        : 'Merged; shape not learned yet — first upload auto-onboards';
                    })
                  }
                >
                  Merge
                </button>
                <button
                  type="button"
                  className={btnGhost}
                  disabled={busy === r.id}
                  onClick={() =>
                    decide(r.id, async () => {
                      await rejectCarrierRequest(r.id);
                      return 'Request rejected';
                    })
                  }
                >
                  Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {decided.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            className="text-xs text-neutral-400 underline hover:text-neutral-600"
            onClick={() => setShowDecided((v) => !v)}
          >
            {showDecided ? 'Hide' : 'Show'} recent decisions ({decided.length})
          </button>
          {showDecided && (
            <ul className="mt-2 space-y-1">
              {decided.map((r) => (
                <li key={r.id} className="text-xs text-neutral-500">
                  <span className="font-medium">{r.requestedName}</span> ({r.accountName}) —{' '}
                  {r.status}
                  {r.resolvedCarrierName ? ` → ${r.resolvedCarrierName}` : ''}
                  {r.decidedBy ? ` by ${r.decidedBy}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Panel>
  );
}
